import React from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import {
  Alert,
  Affix,
  Form,
  Icon,
  Input,
  Button,
  Badge,
  Row,
  Col,
  Checkbox,
  InputNumber,
  DatePicker,
  TimePicker,
  Select,
  Radio,
  Modal,
  Tooltip,
  Switch,
  message,
} from "antd";
import get from "lodash/get";
import { connect } from "react-redux";
import moment from "moment-timezone";
import FileLink from "../../components/FileLink/FileLink";
import PatientProfileDeleteForm from "./PatientProfileDeleteForm";

import {
  updateSubjectRequest,
  createSubjectRequest,
  deleteSubjectRequest,
  activateSubjectRequest,
  lockSubjectRequest,
  unlockSubjectRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getPatientProfileCreatePermission,
  getPatientProfileUpdatePermission,
  getPatientProfileDeletePermission,
  getPatientProfileLockPermission,
  getPatientProfileUnlockPermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import { withParams } from "../../utils/url";
import { range } from "../../utils/range";
import classes from "./PatientProfileForm.css";
import { withNamespaces } from "react-i18next";

const routes = require("../../routes.json");

const { Option } = Select;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
}

const emailRegex = new RegExp(
  /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
);

class EventMessage extends React.Component {
  renderMessagePart(messagePart) {
    if (emailRegex.test(messagePart)) {
      return (
        <a href={`mailto:${messagePart}`} target="_blank">
          {messagePart}
        </a>
      );
    }

    return messagePart;
  }

  render() {
    return (
      <span>
        {this.props.message.split(emailRegex).map((messagePart, index) => (
          <React.Fragment key={index}>
            {this.renderMessagePart(messagePart)}
          </React.Fragment>
        ))}
      </span>
    );
  }
}

class PatientProfileForm extends React.Component {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();

    this.state = {
      isLoading: false,
      isSubmitted: false,
      requestResult: {},
      deleteSubmitModal: false,
      activateSubmitModal: false,
      previewEvents: {},
      showPatientForm: this.props.isNew,
      showPatientEventSummary: false,
      showRegistryForm: false,
      confirmSubmitModal: false,
      locked: false,
      locked_date: null,
    };
  }

  componentDidMount() {
    moment.tz.setDefault(
      this.props.userTimezone || get(this.props, "site.time_zone") || "UTC"
    );

    this.setState({
      locked: get(this.props.profile, "locked"),
      locked_date: get(this.props.profile, "locked_date"),
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.subjectID !== this.props.subjectID) {
      this.setState({
        requestResult: {},
        locked: get(this.props, "profile.locked"),
      });
    }

    if (prevProps.randomized !== this.props.randomized) {
      this.setState({ requestResult: {}, locked: false });
    }
  }

  componentWillUnmount() {
    moment.tz.setDefault();
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      const { getFieldValue } = this.props.form;
      const {
        profile,
        patientProfileCreatePermission,
        patientProfileUpdatePermission,
      } = this.props;

      this.setState({ isSubmitted: true, requestResult: {} });

      if (
        !err &&
        ((patientProfileCreatePermission && !profile) ||
          (patientProfileUpdatePermission && profile))
      ) {
        const age = getFieldValue("age");
        const email = getFieldValue("email");

        if (age > 110 && !this.state.confirmSubmitModal) {
          this.showConfirmModal();
          return;
        }

        this.setState({ isLoading: true });

        try {
          const profile = { ...values };

          if (values.death_date_date && values.death_date_time) {
            profile.death_date = moment(
              `${values.death_date_date.format(
                "YYYY-MM-DD"
              )}T${values.death_date_time.format("HH:mm")}`
            )
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
          }

          if (values.stroke_onsent_date && values.stroke_onsent_time) {
            profile.stroke_onsent = moment(
              `${values.stroke_onsent_date.format(
                "YYYY-MM-DD"
              )}T${values.stroke_onsent_time.format("HH:mm")}`
            )
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
          }
          if (values.baseline_ct_mri_date && values.baseline_ct_mri_time) {
            profile.baseline_ct_mri = moment(
              `${values.baseline_ct_mri_date.format(
                "YYYY-MM-DD"
              )}T${values.baseline_ct_mri_time.format("HH:mm")}`
            )
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
          }
          delete profile.death_date_date;
          delete profile.death_date_time;
          delete profile.stroke_onsent_date;
          delete profile.stroke_onsent_time;
          delete profile.baseline_ct_mri_time;
          delete profile.baseline_ct_mri_date;

          profile.is_consent_given = values.is_consent_given === "true";
          profile.is_deceased = values.is_deceased === "true";
          profile.baseline_occlusion_location =
            values.baseline_occlusion_location !== ""
              ? parseInt(values.baseline_occlusion_location, 10)
              : null;
          profile.aspects_score =
            values.aspects_score !== ""
              ? parseInt(values.aspects_score, 10)
              : null;
          profile.subject_weight =
            values.subject_weight !== ""
              ? parseInt(values.subject_weight, 10)
              : null;
          // profile.is_subject_verified = values.is_subject_verified === true;
          // profile.is_randomization_verified = values.is_randomization_verified === true;
          profile.immune_status = values.immune_status === "true";
          profile.age = age || null;
          profile.email = email || null;
          profile.contact_2_phone_number_type =
            profile.contact_2_phone_number_type || undefined;
          profile.contact_2_relationship_type =
            profile.contact_2_relationship_type || undefined;
          profile.contact_2_email = profile.contact_2_email || undefined;

          profile.randomizationDate = undefined;
          profile.subjectId = undefined;
          profile.birthdate_month = undefined;
          profile.birthdate = `${values.birthdate}-01-01`;

          if (this.props.profile) {
            await updateSubjectRequest(this.props.subjectID, profile);
            setTimeout(() => {
              this.hideConfirmModal();
              this.setState({ isLoading: false, requestResult: {} });
              this.props.onSubmit(this.props.subjectID, true);
            }, 1500);
          } else {
            const { data } = await createSubjectRequest(profile);
            setTimeout(() => {
              this.hideConfirmModal();
              this.props.history.push(
                withParams(routes.admin.patientProfileSingle, {
                  subjectID: get(data, "code"),
                })
              );
              // this.props.onSubmit(this.props.subjectID);
            }, 1500);
          }
          this.setState({
            isLoading: false,
            requestResult: { type: "success", message: this.props.i18n.t('Success') },
          });
        } catch (e) {
          if (get(e, "response.status") === 401) {
            this.props.logout();
            return;
          }
          console.log(e);
          this.setState({
            isLoading: false,
            requestResult: {
              type: "error",
              message: get(e, "response.data.message", this.props.i18n.t("Error")),
            },
          });

          if (get(e, "response.data.errors")) {
            const errors = {};
            Object.keys(get(e, "response.data.errors")).forEach(
              (errorFieldKey) => {
                errors[errorFieldKey] = {
                  value: values[errorFieldKey],
                  errors: get(e, `response.data.errors.${errorFieldKey}`).map(
                    (errorMessage) => new Error(errorMessage)
                  ),
                };
              }
            );
            this.props.form.setFields(errors);
          }
        }
      }
    });
  };

  showDeleteModal = () => {
    this.setState({ deleteSubmitModal: true });
  };
  hideDeleteModal = () => {
    this.setState({ deleteSubmitModal: false });
  };

  showConfirmModal = () => {
    this.setState({ confirmSubmitModal: true });
  };
  hideConfirmModal = () => {
    this.setState({ confirmSubmitModal: false });
  };

  showActivateModal = () => {
    this.setState({ activateSubmitModal: true });
  };
  hideActivateModal = () => {
    this.setState({ activateSubmitModal: false });
  };

  submitDeletePatient = async () => {
    const { patientProfileDeletePermission } = this.props;

    if (patientProfileDeletePermission) {
      try {
        await deleteSubjectRequest(this.props.subjectID);
        this.props.onProfileDelete();
      } catch (e) {
        if (get(e, "response.status") === 401) {
          this.props.logout();
        }
      }
    }
  };

  scrollUp = () => {
    window.scrollTo(0, 0);
  };

  togglePreviewEvents = (eventName) => () => {
    this.setState((state) => {
      const eventStatus = state.previewEvents[eventName] ? false : true;
      return {
        previewEvents: { ...state.previewEvents, [eventName]: eventStatus },
      };
    });
  };

  collapseSection = (sectionName) => () => {
    this.setState((state) => ({ [sectionName]: !state[sectionName] }));
  };

  activateSubject = async () => {
    const { patientProfileDeletePermission } = this.props;

    if (patientProfileDeletePermission) {
      try {
        await activateSubjectRequest(this.props.subjectID);
        this.props.onSubmit(this.props.subjectID);
      } catch (e) {
        console.log(e);
        if (get(e, "response.status") === 401) {
          this.props.logout();
        } else {
          message.error(get(e, "response.data.error", this.props.i18n.t("Error")));
        }
      }
    }
  };

  onSwitchLock = async () => {
    try {
      if (this.state.locked) {
        await unlockSubjectRequest(this.props.subjectID);
        this.setState({ locked: false, locked_date: null });
      } else {
        await lockSubjectRequest(this.props.subjectID);
        this.setState({ locked: true, locked_date: new Date() });
      }
      message.success("Saved");
    } catch (e) {
      message.error(get(e, "response.data.error", this.props.i18n.t("Error")));
    }
  };

  onDone = () => {
    this.props.onSubjectClear();
    this.props.history.push(routes.admin.patientProfile);
  };

  getNow = () => {
    return moment
      .utc(this.props.randomization_date)
      .tz(
        this.props.userTimezone || get(this.props, "site.time_zone") || "UTC"
      );
  };

  render() {
    const {
      isLoading,
      isSubmitted,
      requestResult,
      deleteSubmitModal,
      previewEvents,
      showPatientForm,
      showRegistryForm,
      showPatientEventSummary,
      activateSubmitModal,
      confirmSubmitModal,
      locked,
      locked_date,
    } = this.state;

    const {
      profile,
      dispensingSites,
      patientProfileCreatePermission,
      patientProfileUpdatePermission,
      patientProfileDeletePermission,
      state,
      patientProfileLockPermission,
      patientProfileUnlockPermission,
      isNew,
    } = this.props;
    const {
      getFieldDecorator,
      getFieldError,
      isFieldTouched,
      getFieldValue,
    } = this.props.form;
    const subjectIdError =
      (isFieldTouched("subjectId") || isSubmitted) &&
      getFieldError("subjectId");

    // const patientAge = getFieldValue('birthdate') && getFieldValue('birthdate_month') && moment().diff(`${getFieldValue('birthdate')}-${getFieldValue('birthdate_month')}`, 'years');
    let isEditable =
      (patientProfileCreatePermission && !profile) ||
      (patientProfileUpdatePermission && profile && !locked) ||
      (patientProfileUpdatePermission &&
        profile &&
        profile.locked &&
        profile.allow_to_edit);
    if (state !== "active") {
      isEditable = false;
    }
    if (isNew && patientProfileCreatePermission) {
      isEditable = true;
    }

    return (
      <div className={classes.patientProfileForm} ref={this.containerRef}>
        <Form
          onSubmit={this.handleSubmit}
          className={classes.patientProfileFormWrapper}
        >
          <h4
            className={classnames("headline-default", classes.toggleHeadline)}
            onClick={this.collapseSection("showPatientForm")}
          >
            <Tooltip title={showPatientForm ? "collapse" : "show"}>
              <Icon
                type="arrow-up"
                className={classes.collapseIcon}
                style={{
                  fontSize: "20px",
                  transform: `rotate(${showPatientForm ? "0" : "180deg"})`,
                }}
              />{" "}
              &nbsp;
            </Tooltip>
            <span className={classes.toggleText}>{this.props.i18n.t('NamespacePatientProfileForm:Patient Profile')}</span>
            &nbsp;
            <Badge
              count={profile ? this.props.i18n.t('Edit') : this.props.i18n.t('New')}
              style={{ backgroundColor: "#2968AE" }}
            />
            &nbsp;
            <Badge
              count={state}
              style={{
                backgroundColor: state === "active" ? "#49c850" : "#f00",
              }}
            />
            &nbsp;
            {profile && (
              <Badge
                count={
                  locked
                    ? `${this.props.i18n.t('Locked by')} ${get(
                        this.props,
                        "profile.locked_by_username"
                      )}`
                    : this.props.i18n.t('Unlocked')
                }
                style={{
                  backgroundColor: locked ? "#f00" : "#49c850",
                  textTransform: "none",
                }}
              />
            )}
            &nbsp;
            {locked_date && (
              <Badge
                count={`Locked at ${moment
                  .utc(locked_date)
                  .tz(
                    this.props.userTimezone ||
                      get(this.props, "site.time_zone") ||
                      "UTC"
                  )
                  .format("YYYY-MM-DD HH:mm")} ${
                  this.props.userShortTimezone ||
                  get(this.props, "site.short_tz") ||
                  "UTC"
                }`}
                style={{ backgroundColor: "#f00", textTransform: "none" }}
              />
            )}
          </h4>

          {showPatientForm && (
            <div>
              {!isNew && (
                <Row gutter={8}>
                  <Col lg={24}>
                    <div className={classes.fieldInline}>
                      <label>
                        <b>{this.props.i18n.t('Subject ID')}</b>
                        <Form.Item
                          validateStatus={subjectIdError ? "error" : ""}
                          help={subjectIdError || ""}
                        >
                          {getFieldDecorator("subjectId", {
                            initialValue: get(this.props, "code", ""),
                          })(<Input disabled />)}
                        </Form.Item>
                      </label>
                    </div>
                  </Col>

                  <Col lg={24}>
                    <div className={classes.fieldInline}>
                      <label>
                        <b>{this.props.i18n.t('Randomization Date')}</b>
                        <Form.Item>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {getFieldDecorator("randomizationDate", {
                              initialValue: this.props.randomization_date
                                ? moment
                                    .utc(this.props.randomization_date)
                                    .tz(
                                      this.props.userTimezone ||
                                        get(this.props, "site.time_zone") ||
                                        "UTC"
                                    )
                                : null,
                            })(
                              <DatePicker
                                format="YYYY/MM/DD HH:mm"
                                showTime={{ format: "HH:mm" }}
                                disabled
                              />
                            )}
                            &nbsp;
                            {this.props.userShortTimezone ||
                              get(this.props, "site.short_tz") ||
                              "UTC"}
                          </div>
                        </Form.Item>
                      </label>
                    </div>
                  </Col>
                </Row>
              )}

              {/* <Row gutter={8}>
              <Col md={8}>
                <label>
                  <b>AHS Reference #</b>
                  <Form.Item>
                    {getFieldDecorator('ahs_profile_id', {
                      initialValue: get(this.props, 'profile.ahs_profile_id', '')
                    })(
                      <Input disabled={!isEditable} />,
                    )}
                  </Form.Item>
                </label>
              </Col>
            </Row> */}
              <Row gutter={8}>
                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Birth year*')}
                      {get(this.props, "profile.birthdate") !==
                        get(this.props, "profile_copy.birthdate") && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${
                            get(this.props, "profile_copy.birthdate", "").split(
                              "-"
                            )[0]
                          }`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "4px",
                        }}
                      >
                        {getFieldDecorator("birthdate", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(
                            this.props,
                            "profile.birthdate",
                            ""
                          ).split("-")[0],
                        })(
                          <InputNumber
                            disabled={!isEditable}
                            min={1900}
                            max={new Date().getFullYear()}
                            style={{ width: "100%" }}
                            onChange={(value) => {
                              const patientAge = moment().diff(
                                moment(value, "YYYY"),
                                "years"
                              );
                              this.props.form.setFieldsValue({
                                age: Number.isInteger(patientAge)
                                  ? patientAge
                                  : null,
                              });
                            }}
                          />
                        )}
                      </div>
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Age*')}
                      {get(this.props, "profile.age") !==
                        get(this.props, "profile_copy.age") && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${get(
                            this.props,
                            "profile_copy.age"
                          )}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("age", {
                        initialValue: get(this.props, "profile.age", ""),
                      })(<Input disabled />)}
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Sex*')}
                      {get(this.props, "profile.sex") !==
                        get(this.props, "profile_copy.sex") && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${
                            get(this.props, "profile_copy.sex") === 0
                              ? "Female"
                              : "Male"
                          }`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("sex", {
                        initialValue: [0, 1].includes(
                          get(this.props, "profile.sex")
                        )
                          ? get(this.props, "profile.sex").toString()
                          : null,
                      })(
                        <Select disabled={!isEditable}>
                          <Option value="1">{this.props.i18n.t('Male')}</Option>
                          <Option value="0">{this.props.i18n.t('Female')}</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Baseline NIHSS*')}
                      {get(this.props, "profile.baseline_nihss") !==
                        get(this.props, "profile_copy.baseline_nihss") && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${get(
                            this.props,
                            "profile_copy.baseline_nihss"
                          )}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      <div>
                        {getFieldDecorator("baseline_nihss", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(
                            this.props,
                            "profile.baseline_nihss"
                          ),
                        })(
                          <InputNumber
                            placeholder="6 - 42"
                            disabled={!isEditable}
                            min={6}
                            max={42}
                          />
                        )}
                      </div>
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('NamespacePatientProfileForm:Baseline Occlusion Location*')}
                      {get(
                        this.props,
                        "profile.baseline_occlusion_location"
                      ) !==
                        get(
                          this.props,
                          "profile_copy.baseline_occlusion_location"
                        ) && (
                        <Tooltip
                          placement="top"
                          title={`${this.props.i18n.t('NamespacePatientProfileForm:Randomization value')}: ${
                            get(
                              this.props,
                              "profile_copy.baseline_occlusion_location"
                            ) === 1
                              ? "ICA"
                              : get(
                                  this.props,
                                  "profile_copy.baseline_occlusion_location"
                                ) === 2
                              ? "M1"
                              : "M2"
                          }`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("baseline_occlusion_location", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(
                          this.props,
                          "profile.baseline_occlusion_location",
                          null
                        ),
                      })(
                        <Select disabled={!isEditable}>
                          <Option value={1}>ICA</Option>
                          <Option value={2}>M1</Option>
                          <Option value={3}>M2</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>
                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('ASPECTS*')}
                      {get(this.props, "profile.aspects_score") !==
                        get(this.props, "profile_copy.aspects_score") && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${
                            get(this.props, "profile_copy.aspects_score") === 1
                              ? "8-10"
                              : "5-7"
                          }`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("aspects_score", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(
                          this.props,
                          "profile.aspects_score",
                          null
                        ),
                      })(
                        <Select disabled={!isEditable}>
                          <Option value={0}>5-7</Option>
                          <Option value={1}> 8-10</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Weight*')}
                      {get(this.props, "profile.subject_weight") !==
                        get(this.props, "profile_copy.subject_weight") && (
                        <Tooltip
                          placement="top"
                          title={`${this.props.i18n.t('NamespacePatientProfileForm:Randomization value')}: ${get(
                            this.props,
                            "profile_copy.subject_weight"
                          )}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("subject_weight", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(
                          this.props,
                          "profile.subject_weight",
                          ""
                        ),
                      })(<InputNumber disabled={!isEditable} min={1} />)}
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('NamespacePatientProfileForm:Randomization Weight Unit*')}
                      {get(this.props, "profile.subject_weight_unit") !==
                        get(this.props, "profile_copy.subject_weight_unit") && (
                        <Tooltip
                          placement="top"
                          title={`${this.props.i18n.t('NamespacePatientProfileForm:Randomization value')}: ${get(
                            this.props,
                            "profile_copy.subject_weight_unit"
                          )}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("subject_weight_unit", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(
                          this.props,
                          "profile.subject_weight_unit"
                        ),
                      })(
                        <Select disabled={!isEditable}>
                          <Option value="kgs">Kgs</Option>
                          <Option value="lbs">Lbs</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('NamespacePatientProfileForm:How was this weight at randomization determined?*')}
                      {get(this.props, "profile.subject_weight_determined") !==
                        get(
                          this.props,
                          "profile_copy.subject_weight_determined"
                        ) && (
                        <Tooltip
                          placement="top"
                          title={`Randomization value: ${get(
                            this.props,
                            "profile_copy.subject_weight_determined"
                          )}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                    <Form.Item>
                      {getFieldDecorator("subject_weight_determined", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(
                          this.props,
                          "profile.subject_weight_determined"
                        ),
                      })(
                        <Select disabled={!isEditable}>
                          <Option value="actual">{this.props.i18n.t('Actual')}</Option>
                          <Option value="estimated">{this.props.i18n.t('Estimated')}</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Stroke onset date*')}
                      {get(this.props, "profile.stroke_onsent") !==
                        get(this.props, "profile_copy.stroke_onsent") && (
                        <Tooltip
                          placement="top"
                          title={`${this.props.i18n.t('NamespacePatientProfileForm:Randomization value')}: ${moment
                            .utc(get(this.props, "profile_copy.stroke_onsent"))
                            .tz(
                              this.props.userTimezone ||
                                get(this.props, "site.time_zone") ||
                                "UTC"
                            )
                            .format("YYYY-MM-DD HH:mm")}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "4px",
                    }}
                  >
                    <Form.Item>
                      <div style={{ width: "120px" }}>
                        {getFieldDecorator("stroke_onsent_date", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(this.props, "profile.stroke_onsent")
                            ? moment
                                .utc(this.props.profile.stroke_onsent)
                                .tz(
                                  this.props.userTimezone ||
                                    get(this.props, "site.time_zone") ||
                                    "UTC"
                                )
                            : null,
                        })(
                          <DatePicker
                            disabled={!isEditable}
                            format="YYYY/MM/DD"
                            placeholder="yyyy-mm-dd"
                            disabledDate={(currentDate) => {
                              const now = this.getNow();
                              const currentTimeHour = now.hour();
                              return (
                                moment(currentDate.format("YYYY-MM-DD")) >
                                  moment(now.format("YYYY-MM-DD")) ||
                                moment(currentDate.format("YYYY-MM-DD")) <
                                  moment(now.format("YYYY-MM-DD")).subtract(
                                    currentTimeHour > 12 ? 0 : 1,
                                    "days"
                                  )
                              );
                            }}
                            showToday={false}
                          />
                        )}
                      </div>
                    </Form.Item>
                    &nbsp;&nbsp;
                    <Form.Item>
                      <div style={{ width: "120px" }}>
                        {getFieldDecorator("stroke_onsent_time", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(this.props, "profile.stroke_onsent")
                            ? moment
                                .utc(this.props.profile.stroke_onsent)
                                .tz(
                                  this.props.userTimezone ||
                                    get(this.props, "site.time_zone") ||
                                    "UTC"
                                )
                            : null,
                        })(
                          <TimePicker
                            disabled={
                              !isEditable ||
                              !getFieldValue("stroke_onsent_date")
                            }
                            format="HH:mm"
                            placeholder="HH:mm"
                            disabledHours={() => {
                              const now = this.getNow();

                              const currentTimeDate = now.date();
                              const strokeDate = moment(
                                getFieldValue("stroke_onsent_date")
                              ).date();
                              const currentTimeHour = now.hour();

                              if (currentTimeDate === strokeDate) {
                                return [
                                  ...range(0, currentTimeHour - 12),
                                  ...range(currentTimeHour + 1, 25),
                                ];
                              } else {
                                return range(0, currentTimeHour + 12);
                              }
                            }}
                            disabledMinutes={() => {
                              const now = this.getNow();
                              const strokeDate = getFieldValue(
                                "stroke_onsent_date"
                              );
                              const strokeTime = getFieldValue(
                                "stroke_onsent_time"
                              );

                              if (
                                strokeTime &&
                                now.date() === strokeDate.date() &&
                                strokeTime.hour() === now.hour()
                              ) {
                                return range(now.minute() + 1, 60);
                              }
                              if (
                                strokeTime &&
                                now.date() !== strokeDate.date() &&
                                strokeTime.hour() ===
                                  now.subtract(12, "hours").hour()
                              ) {
                                return range(0, now.minute());
                              }
                              if (!strokeTime) {
                                return range(0, 60);
                              }
                            }}
                            showToday={false}
                          />
                        )}
                      </div>
                    </Form.Item>
                    &nbsp;&nbsp;&nbsp;
                    <Form.Item>
                      {this.props.userShortTimezone ||
                        get(this.props, "site.short_tz") ||
                        "UTC"}
                    </Form.Item>
                  </div>
                </Col>

                {((getFieldValue("stroke_onsent_date") &&
                  getFieldValue("stroke_onsent_time")) ||
                  get(this.props, "profile.stroke_onsent")) && (
                  <Col md={24}>
                    <div style={{ padding: "0 0 30px" }}>
                      <b>{this.props.i18n.t('NamespacePatientProfileForm:Stroke onset to randomization time (minutes)')}:</b>&nbsp;
                      {(() => {
                        const now = this.getNow();
                        let strokeDate;

                        if (
                          getFieldValue("stroke_onsent_date") &&
                          getFieldValue("stroke_onsent_time")
                        ) {
                          strokeDate = moment(
                            `${getFieldValue("stroke_onsent_date").format(
                              "YYYY-MM-DD"
                            )} ${getFieldValue("stroke_onsent_time").format(
                              "HH:mm"
                            )}`
                          );
                        } else if (get(this.props, "profile.stroke_onsent")) {
                          strokeDate = moment
                            .utc(get(this.props, "profile.stroke_onsent"))
                            .tz(
                              this.props.userTimezone ||
                                get(this.props, "site.time_zone") ||
                                "UTC"
                            );
                        }

                        return now.diff(strokeDate, "minutes");
                      })()}
                    </div>
                  </Col>
                )}

                <Col md={24}>
                  <label>
                    <b className={classes.labelText}>
                      {this.props.i18n.t('Baseline CT/MRI Date*')}
                      {get(this.props, "profile.baseline_ct_mri") !==
                        get(this.props, "profile_copy.baseline_ct_mri") && (
                        <Tooltip
                          placement="top"
                          title={`${this.props.i18n.t('NamespacePatientProfileForm:Profile value')}: ${moment
                            .utc(
                              get(this.props, "profile_copy.baseline_ct_mri")
                            )
                            .tz(
                              this.props.userTimezone ||
                                get(this.props, "site.time_zone") ||
                                "UTC"
                            )
                            .format("YYYY-MM-DD HH:mm")}`}
                        >
                          <div className={classes.matchInfo} />
                        </Tooltip>
                      )}
                    </b>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "4px",
                    }}
                  >
                    <Form.Item>
                      <div style={{ width: "120px" }}>
                        {getFieldDecorator("baseline_ct_mri_date", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(
                            this.props,
                            "profile.baseline_ct_mri"
                          )
                            ? moment
                                .utc(this.props.profile.baseline_ct_mri)
                                .tz(
                                  this.props.userTimezone ||
                                    get(this.props, "site.time_zone") ||
                                    "UTC"
                                )
                            : null,
                        })(
                          <DatePicker
                            disabled={!isEditable}
                            format="YYYY/MM/DD"
                            placeholder="yyyy-mm-dd"
                            disabledDate={(currentDate) => {
                              const now = this.getNow();
                              const currentTimeHour = now.hour();
                              return (
                                moment(currentDate.format("YYYY-MM-DD")) >
                                  moment(now.format("YYYY-MM-DD")) ||
                                moment(currentDate.format("YYYY-MM-DD")) <
                                  moment(now.format("YYYY-MM-DD")).subtract(
                                    currentTimeHour > 12 ? 0 : 1,
                                    "days"
                                  )
                              );
                            }}
                            showToday={false}
                          />
                        )}
                      </div>
                    </Form.Item>
                    &nbsp;&nbsp;
                    <Form.Item>
                      <div style={{ width: "120px" }}>
                        {getFieldDecorator("baseline_ct_mri_time", {
                          rules: [{ required: true, message: this.props.i18n.t("Required") }],
                          initialValue: get(
                            this.props,
                            "profile.baseline_ct_mri"
                          )
                            ? moment
                                .utc(this.props.profile.baseline_ct_mri)
                                .tz(
                                  this.props.userTimezone ||
                                    get(this.props, "site.time_zone") ||
                                    "UTC"
                                )
                            : null,
                        })(
                          <TimePicker
                            disabled={
                              !isEditable ||
                              !getFieldValue("baseline_ct_mri_date")
                            }
                            format="HH:mm"
                            placeholder="HH:mm"
                            disabledHours={() => {
                              const now = this.getNow();

                              const currentTimeDate = now.date();
                              const strokeDate = moment(
                                getFieldValue("baseline_ct_mri_date")
                              ).date();
                              const currentTimeHour = now.hour();

                              if (currentTimeDate === strokeDate) {
                                return [
                                  ...range(0, currentTimeHour - 12),
                                  ...range(currentTimeHour + 1, 25),
                                ];
                              } else {
                                return range(0, currentTimeHour + 12);
                              }
                            }}
                            disabledMinutes={() => {
                              const now = this.getNow();
                              const baselineDate = getFieldValue(
                                "baseline_ct_mri_date"
                              );
                              const baselineTime = getFieldValue(
                                "baseline_ct_mri_time"
                              );

                              if (
                                baselineTime &&
                                now.date() === baselineDate.date() &&
                                baselineTime.hour() === now.hour()
                              ) {
                                return range(now.minute() + 1, 60);
                              }
                              if (
                                baselineTime &&
                                now.date() !== baselineDate.date() &&
                                baselineTime.hour() ===
                                  now.subtract(12, "hours").hour()
                              ) {
                                return range(0, now.minute());
                              }

                              if (!baselineTime) {
                                return range(0, 60);
                              }
                            }}
                            showToday={false}
                          />
                        )}
                      </div>
                    </Form.Item>
                    &nbsp;&nbsp;&nbsp;
                    <Form.Item>
                      {this.props.userShortTimezone ||
                        get(this.props, "site.short_tz") ||
                        "UTC"}
                    </Form.Item>
                  </div>
                </Col>

                {((getFieldValue("baseline_ct_mri_date") &&
                  getFieldValue("baseline_ct_mri_time")) ||
                  get(this.props, "profile.baseline_ct_mri")) && (
                  <Col md={24}>
                    <div style={{ padding: "0 0 30px" }}>
                      <b>{this.props.i18n.t('NamespacePatientProfileForm:Baseline CT/MRI to randomization time (minutes)')}:</b>&nbsp;
                      {(() => {
                        const now = this.getNow();
                        let strokeDate;

                        if (
                          getFieldValue("baseline_ct_mri_date") &&
                          getFieldValue("baseline_ct_mri_time")
                        ) {
                          strokeDate = moment(
                            `${getFieldValue("baseline_ct_mri_date").format(
                              "YYYY-MM-DD"
                            )} ${getFieldValue("baseline_ct_mri_time").format(
                              "HH:mm"
                            )}`
                          );
                        } else if (get(this.props, "profile.baseline_ct_mri")) {
                          strokeDate = moment
                            .utc(get(this.props, "profile.baseline_ct_mri"))
                            .tz(
                              this.props.userTimezone ||
                                get(this.props, "site.time_zone") ||
                                "UTC"
                            );
                        }

                        return now.diff(strokeDate, "minutes");
                      })()}
                    </div>
                  </Col>
                )}
              </Row>

              {/*!isNew && (
                <Row gutter={8}>
                  <Col md={8}>
                    <div className={classes.fieldInline}>
                      <div>
                        <b>Patient Deceased?</b>
                        <Form.Item>
                          {getFieldDecorator("is_deceased", {
                            initialValue: get(
                              this.props,
                              "profile.is_deceased",
                              ""
                            ).toString(),
                          })(
                            <Radio.Group disabled={!isEditable}>
                              <Radio value="true">Yes</Radio>
                              <Radio value="false">No</Radio>
                            </Radio.Group>
                          )}
                        </Form.Item>
                      </div>
                    </div>
                  </Col>

                  {getFieldValue("is_deceased") === "true" && (
                    <Col md={24}>
                      <label>
                        <b>Decease date*</b>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "4px",
                        }}
                      >
                        <Form.Item>
                          <div style={{ width: "120px" }}>
                            {getFieldDecorator("death_date_date", {
                              rules: [{ required: true, message: "Required" }],
                              initialValue: get(
                                this.props,
                                "profile.death_date"
                              )
                                ? moment
                                    .utc(this.props.profile.death_date)
                                    .tz(
                                      this.props.userTimezone ||
                                        get(this.props, "site.time_zone") ||
                                        "UTC"
                                    )
                                : null,
                            })(
                              <DatePicker
                                disabled={!isEditable}
                                format="YYYY/MM/DD"
                                placeholder="yyyy-mm-dd"
                                disabledDate={(currentDate) => {
                                  const now = moment();
                                  return (
                                    moment(currentDate.format("YYYY-MM-DD")) >
                                    moment(now.format("YYYY-MM-DD"))
                                  );
                                }}
                                showToday={false}
                              />
                            )}
                          </div>
                        </Form.Item>
                        &nbsp;&nbsp;
                        <Form.Item>
                          <div style={{ width: "120px" }}>
                            {getFieldDecorator("death_date_time", {
                              rules: [{ required: true, message: "Required" }],
                              initialValue: get(
                                this.props,
                                "profile.death_date"
                              )
                                ? moment
                                    .utc(this.props.profile.death_date)
                                    .tz(
                                      this.props.userTimezone ||
                                        get(this.props, "site.time_zone") ||
                                        "UTC"
                                    )
                                : null,
                            })(
                              <TimePicker
                                disabled={
                                  !isEditable ||
                                  !getFieldValue("death_date_date")
                                }
                                format="HH:mm"
                                placeholder="HH:mm"
                                disabledHours={() => {
                                  const now = this.getNow();

                                  const currentTimeDate = now.date();
                                  const strokeDate = moment(
                                    getFieldValue("death_date_date")
                                  ).date();
                                  const currentTimeHour = now.hour();

                                  if (currentTimeDate === strokeDate) {
                                    return [
                                      ...range(0, currentTimeHour - 12),
                                      ...range(currentTimeHour + 1, 25),
                                    ];
                                  }
                                }}
                                disabledMinutes={() => {
                                  const now = this.getNow();
                                  const baselineDate = getFieldValue(
                                    "death_date_date"
                                  );
                                  const baselineTime = getFieldValue(
                                    "death_date_time"
                                  );

                                  if (
                                    baselineTime &&
                                    now.date() === baselineDate.date() &&
                                    baselineTime.hour() === now.hour()
                                  ) {
                                    return range(now.minute() + 1, 60);
                                  }
                                }}
                                showToday={false}
                              />
                            )}
                          </div>
                        </Form.Item>
                        &nbsp;&nbsp;&nbsp;
                        <Form.Item>
                          {this.props.userShortTimezone ||
                            get(this.props, "site.short_tz") ||
                            "UTC"}
                        </Form.Item>
                      </div>
                    </Col>
                  )}
                </Row>
              )*/}

              {!isNew && (
                <Row gutter={8}>
                  <Col lg={24}>
                    <b>{this.props.i18n.t('Comments')}:</b>
                    <div>
                      <label>
                        <Form.Item>
                          {getFieldDecorator("comments", {
                            initialValue: get(this.props, "profile.comments"),
                          })(
                            <Input.TextArea disabled={!isEditable} rows="6" />
                          )}
                        </Form.Item>
                      </label>
                    </div>
                  </Col>
                </Row>
              )}

              <Affix
                offsetBottom={0}
                style={{ zIndex: 1, position: "relative" }}
              >
                <div className={classes.saveWrapper}>
                  {requestResult.type && (
                    <Alert
                      message={requestResult.message}
                      type={requestResult.type}
                      className={classes.submitResult}
                    />
                  )}

                  <Form.Item className={classes.formActions}>
                    {this.props.profile && (
                      <Tooltip
                        placement="topLeft"
                        title={this.props.i18n.t('NamespacePatientProfileForm:Lock / Unlock the Subject Profile')}
                      >
                        {this.props.i18n.t('NamespacePatientProfileForm:Lock status')}: &nbsp;
                        <Switch
                          disabled={
                            (!patientProfileUnlockPermission && locked) ||
                            (!patientProfileLockPermission && !locked)
                          }
                          checked={locked}
                          onChange={this.onSwitchLock}
                        />
                      </Tooltip>
                    )}

                    {patientProfileDeletePermission && isEditable && !isNew && (
                      <PatientProfileDeleteForm subjectID={this.props.subjectID} locked={locked} onProfileDelete={this.props.onProfileDelete} />
                      /*<Button
                        type="primary"
                        size="large"
                        htmlType="button"
                        className={classes.deleteButton}
                        onClick={this.showDeleteModal}
                      >
                        {this.props.i18n.t('Deactivate')}
                      </Button>*/
                    )}
                    {patientProfileDeletePermission &&
                      state !== "active" &&
                      !isNew && (
                        <Button
                          type="primary"
                          size="large"
                          htmlType="button"
                          onClick={this.showActivateModal}
                        >
                          {this.props.i18n.t('Activate')}
                        </Button>
                      )}
                    {!isNew && (
                      <Button
                        size="large"
                        htmlType="button"
                        onClick={this.onDone}
                      >
                        {this.props.i18n.t('Done')}
                      </Button>
                    )}
                    {isEditable && (
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        className={classes.submitButton}
                      >
                        {this.props.i18n.t('Save')}
                        {isLoading && <Icon type="loading" />}
                      </Button>
                    )}
                  </Form.Item>
                </div>
              </Affix>
            </div>
          )}
        </Form>

        {!isNew && (
            <div className={classes.patientProfileFormWrapper} style={{marginBottom: 0}}>
              <h4
                  className={classnames("headline-default", classes.toggleHeadline)}
                  onClick={this.collapseSection("showPatientEventSummary")}
            >
              <Tooltip title={showPatientEventSummary ? "collapse" : "show"}>
                <Icon
                  type="arrow-up"
                  className={classes.collapseIcon}
                  style={{
                    fontSize: "20px",
                    transform: `rotate(${
                      showPatientEventSummary ? "0" : "180deg"
                    })`,
                  }}
                />{" "}
                &nbsp;
              </Tooltip>

              <span className={classes.toggleText}>{this.props.i18n.t('NamespacePatientProfileForm:Patient Event Summary')}</span>
            </h4>

            {showPatientEventSummary && (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <td>{this.props.i18n.t('Event')}</td>
                    <td>{this.props.i18n.t('Status')}</td>
                    <td>{this.props.i18n.t('NamespacePatientProfileForm:Occurred at site')}</td>
                    <td>{this.props.i18n.t('NamespacePatientProfileForm:Data entered / Audit Trial')}</td>
                    <td>{this.props.i18n.t('Actions')}</td>
                  </tr>
                </thead>
                <tbody>
                  {this.props.events_summary_report &&
                    this.props.events_summary_report.map((item, i) => (
                      <tr key={i}>
                        <td>{item.event}</td>
                        <td>
                          {item.status === "green" && (
                            <Icon
                              type="check-square"
                              className={classes.checkIcon}
                            />
                          )}
                          {item.status === "red" && (
                            <Icon
                              type="warning"
                              className={classes.warningIcon}
                            />
                          )}
                          {item.status === "orange" && (
                            <Icon
                              type="clock-circle"
                              className={classes.clockIcon}
                            />
                          )}
                          {item.status === "gray" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                          {item.status === "pending" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                        </td>
                        <td>{item.site}</td>
                        <td>
                          <div style={{ maxWidth: "500px" }}>
                            <p>
                              <EventMessage message={item.event_comments} />
                            </p>

                            {previewEvents[get(item, "event")] && (
                              <div>
                                {get(item, "other_details.messages", []).map(
                                  (message, index) => (
                                    <p key={index}>{message}</p>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={classes.tableAction}>
                            {["registry_id"].includes(item.event) &&
                              state === "active" && (
                                <Icon
                                  type="edit"
                                  theme="filled"
                                  onClick={this.scrollUp}
                                />
                              )}
                            {["sae"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.saeReportingSignle,
                                    { subjectID: this.props.subjectID }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["validation"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.validateRandomizationAdmin,
                                    {
                                      subjectID: this.props.code,
                                      site: get(this.props, "site.code"),
                                    }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["deviation"].includes(item.event) &&
                              state === "active" && (
                                <Link to="/admin/validate-randomization">
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["randomize"].includes(item.event) &&
                              state === "active" && (
                                <Icon
                                  type="edit"
                                  theme="filled"
                                  onClick={this.scrollUp}
                                />
                              )}
                            {get(item, "other_details.document_link") && (
                              <FileLink url={get(item, "other_details.document_link")} />
                            )}
                            {get(item, "other_details.messages") && (
                              <Icon
                                type={
                                  previewEvents[get(item, "event")]
                                    ? "eye-invisible"
                                    : "eye"
                                }
                                theme="filled"
                                onClick={this.togglePreviewEvents(
                                  get(item, "event")
                                )}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <Modal
          title={this.props.i18n.t('NamespacePatientProfileForm:Confirm submit')}
          visible={confirmSubmitModal}
          onOk={this.handleSubmit}
          onCancel={this.hideConfirmModal}
        >
          <p>{this.props.i18n.t('NamespacePatientProfileForm:Confirm submit action')}</p>
          <p>{this.props.i18n.t('NamespacePatientProfileForm:* Patient age looks wrong')}</p>

          {requestResult.type && (
            <Alert
              message={requestResult.message}
              type={requestResult.type}
              className={classes.submitResult}
            />
          )}
        </Modal>

        {/*<Modal
          title={this.props.i18n.t('NamespacePatientProfileForm:Subject deactivation')}
          visible={deleteSubmitModal}
          onOk={this.submitDeletePatient}
          onCancel={this.hideDeleteModal}
        >
          <p>{this.props.i18n.t('NamespacePatientProfileForm:Confirm patient deactivate action')}</p>
        </Modal>*/}

        <Modal
          title={this.props.i18n.t("NamespacePatientProfileForm:Subject activation")}
          visible={activateSubmitModal}
          onOk={this.activateSubject}
          onCancel={this.hideActivateModal}
        >
          <p>{this.props.i18n.t('NamespacePatientProfileForm:Confirm patient activate action')}</p>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  patientProfileCreatePermission: getPatientProfileCreatePermission(state),
  patientProfileUpdatePermission: getPatientProfileUpdatePermission(state),
  patientProfileDeletePermission: getPatientProfileDeletePermission(state),
  patientProfileLockPermission: getPatientProfileLockPermission(state),
  patientProfileUnlockPermission: getPatientProfileUnlockPermission(state),
});

const mapDispatchToProps = { logout };

export default Form.create({ name: "patientProfile" })(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(PatientProfileForm))
);



// WEBPACK FOOTER //
// ./src/components/PatientProfileForm/PatientProfileForm.js